import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import channelsService from '@/api/services/channelsService';
import type { Channel, ChannelsState } from '@/types/channel';

// Async thunks for API operations
export const fetchChannels = createAsyncThunk(
  'channels/fetchChannels',
  async (_, { rejectWithValue }) => {
    try {
      const response = await channelsService.fetchChannels();
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch channels');
    }
  }
);

export const fetchChatChannels = createAsyncThunk(
  'channels/fetchChatChannels',
  async (_, { rejectWithValue }) => {
    try {
      const channels = await channelsService.fetchChatChannels();
      return channels;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch chat channels');
    }
  }
);

export const fetchEmailChannels = createAsyncThunk(
  'channels/fetchEmailChannels',
  async (_, { rejectWithValue }) => {
    try {
      const channels = await channelsService.fetchEmailChannels();
      return channels;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch email channels');
    }
  }
);

const initialState: ChannelsState = {
  channels: [],
  loading: false,
  error: null,
  areChannelsLoaded: false
};

export const channelsSlice = createSlice({
  name: 'channels',
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setChannels: (state, action: PayloadAction<Channel[]>) => {
      state.channels = action.payload;
      state.loading = false;
      state.areChannelsLoaded = true;
    },
    resetChannels: (state) => {
      state.channels = [];
      state.loading = false;
      state.error = null;
      state.areChannelsLoaded = false;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch Channels
      .addCase(fetchChannels.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchChannels.fulfilled, (state, action) => {
        state.channels = action.payload;
        state.loading = false;
        state.areChannelsLoaded = true;
      })
      .addCase(fetchChannels.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Fetch Chat Channels
      .addCase(fetchChatChannels.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchChatChannels.fulfilled, (state, action) => {
        // Filter existing channels to keep email channels and add new chat channels
        const existingEmailChannels = state.channels.filter(channel => channel.type === 'email');
        state.channels = [...existingEmailChannels, ...action.payload];
        state.loading = false;
        state.areChannelsLoaded = true;
      })
      .addCase(fetchChatChannels.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Fetch Email Channels
      .addCase(fetchEmailChannels.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchEmailChannels.fulfilled, (state, action) => {
        // Filter existing channels to keep chat channels and add new email channels
        const existingChatChannels = state.channels.filter(channel => channel.type === 'chat');
        state.channels = [...existingChatChannels, ...action.payload];
        state.loading = false;
        state.areChannelsLoaded = true;
      })
      .addCase(fetchEmailChannels.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { setLoading, setError, setChannels, resetChannels } = channelsSlice.actions;
export default channelsSlice.reducer; 